import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

export default function ThreeBackground({ height = 600 }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const h = container.clientHeight || height;

    // Scene setup
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0xf3f0e8, 0.0015);

    const camera = new THREE.PerspectiveCamera(60, width / h, 1, 2000);
    camera.position.set(0, 0, 400);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Colors
    const GREEN = new THREE.Color(0x6B8E23);
    const TEAL = new THREE.Color(0x4682B4);
    const GOLD = new THREE.Color(0xDAA520);
    const PALE_GREEN = new THREE.Color(0x8FBC8F);

    // ── DNA Helix ──
    const dnaGroup = new THREE.Group();
    const helixPoints = 80;
    const helixRadius = 30;
    const helixHeight = 500;
    const strandGeom = new THREE.SphereGeometry(2.2, 8, 8);
    const strandMatA = new THREE.MeshBasicMaterial({ color: GREEN, transparent: true, opacity: 0.5 });
    const strandMatB = new THREE.MeshBasicMaterial({ color: TEAL, transparent: true, opacity: 0.5 });

    for (let i = 0; i < helixPoints; i++) {
      const t = (i / helixPoints) * Math.PI * 6;
      const y = (i / helixPoints) * helixHeight - helixHeight / 2;

      const sphereA = new THREE.Mesh(strandGeom, strandMatA);
      sphereA.position.set(Math.cos(t) * helixRadius, y, Math.sin(t) * helixRadius);
      dnaGroup.add(sphereA);

      const sphereB = new THREE.Mesh(strandGeom, strandMatB);
      sphereB.position.set(Math.cos(t + Math.PI) * helixRadius, y, Math.sin(t + Math.PI) * helixRadius);
      dnaGroup.add(sphereB);

      // Rungs every 4 points
      if (i % 4 === 0) {
        const rungGeom = new THREE.CylinderGeometry(0.6, 0.6, helixRadius * 2, 4);
        const rungMat = new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0.2 });
        const rung = new THREE.Mesh(rungGeom, rungMat);
        rung.position.set(0, y, 0);
        rung.rotation.z = Math.PI / 2;
        rung.rotation.y = t;
        dnaGroup.add(rung);
      }
    }
    dnaGroup.position.set(-180, 0, -100);
    dnaGroup.rotation.z = 0.2;
    scene.add(dnaGroup);

    // ── Floating Cells / Molecules ──
    const cells = [];
    const cellGeom = new THREE.SphereGeometry(1, 12, 12);

    for (let i = 0; i < 120; i++) {
      const size = 2 + Math.random() * 8;
      const geom = new THREE.SphereGeometry(size, 12, 12);
      const colors = [GREEN, TEAL, GOLD, PALE_GREEN];
      const color = colors[Math.floor(Math.random() * colors.length)];
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.06 + Math.random() * 0.12,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set(
        (Math.random() - 0.5) * 900,
        (Math.random() - 0.5) * 600,
        (Math.random() - 0.5) * 400 - 100,
      );
      mesh.userData = {
        speed: 0.1 + Math.random() * 0.3,
        offset: Math.random() * Math.PI * 2,
        driftX: (Math.random() - 0.5) * 0.15,
        driftY: (Math.random() - 0.5) * 0.1,
      };
      scene.add(mesh);
      cells.push(mesh);
    }

    // ── Pulse Ring (heartbeat effect) ──
    const pulseRings = [];
    for (let i = 0; i < 3; i++) {
      const ringGeom = new THREE.RingGeometry(40 + i * 30, 42 + i * 30, 64);
      const ringMat = new THREE.MeshBasicMaterial({
        color: GREEN,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
      });
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.position.set(200, -50, -150);
      ring.userData = { phase: i * 1.2 };
      scene.add(ring);
      pulseRings.push(ring);
    }

    // ── Connecting Lines (network) ──
    const linesMat = new THREE.LineBasicMaterial({ color: GREEN, transparent: true, opacity: 0.04 });
    const networkGroup = new THREE.Group();
    const nodePositions = [];
    for (let i = 0; i < 30; i++) {
      nodePositions.push(new THREE.Vector3(
        (Math.random() - 0.5) * 700,
        (Math.random() - 0.5) * 500,
        (Math.random() - 0.5) * 200 - 150,
      ));
    }
    for (let i = 0; i < nodePositions.length; i++) {
      for (let j = i + 1; j < nodePositions.length; j++) {
        if (nodePositions[i].distanceTo(nodePositions[j]) < 200) {
          const geom = new THREE.BufferGeometry().setFromPoints([nodePositions[i], nodePositions[j]]);
          const line = new THREE.Line(geom, linesMat);
          networkGroup.add(line);
        }
      }
    }
    scene.add(networkGroup);

    // ── Animation Loop ──
    let time = 0;
    let animId;

    const animate = () => {
      animId = requestAnimationFrame(animate);
      time += 0.008;

      // Rotate DNA
      dnaGroup.rotation.y += 0.003;

      // Float cells
      cells.forEach(cell => {
        const d = cell.userData;
        cell.position.x += d.driftX;
        cell.position.y += Math.sin(time * d.speed + d.offset) * 0.15;
        // Wrap around
        if (cell.position.x > 500) cell.position.x = -500;
        if (cell.position.x < -500) cell.position.x = 500;
      });

      // Pulse rings (heartbeat)
      pulseRings.forEach(ring => {
        const phase = (time * 0.8 + ring.userData.phase) % 4;
        if (phase < 1.5) {
          const t = phase / 1.5;
          ring.scale.set(1 + t * 2, 1 + t * 2, 1);
          ring.material.opacity = 0.12 * (1 - t);
        } else {
          ring.material.opacity = 0;
        }
      });

      // Gentle camera sway
      camera.position.x = Math.sin(time * 0.15) * 15;
      camera.position.y = Math.cos(time * 0.1) * 10;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
    };
    animate();

    // Resize handler
    const onResize = () => {
      const w = container.clientWidth;
      const newH = container.clientHeight || height;
      camera.aspect = w / newH;
      camera.updateProjectionMatrix();
      renderer.setSize(w, newH);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [height]);

  return (
    <div
      ref={mountRef}
      data-testid="three-bg"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
    />
  );
}
